import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { FaImage } from 'react-icons/fa';
import { ABILITY_COLORS } from '../../api/models';
import { backendUrl } from '@common/contracts';
import './RandomEncountersSettings.scss'; // Using shared styles with resource scanner
const RandomEncountersSettings = ({ coords, levelId: _levelId, settings, availableEncounters, onSettingsChange }) => {
    // Local state to track changes
    const [enabled, setEnabled] = useState(settings.enabled || false);
    const [probability, setProbability] = useState(settings.probability || 10);
    const [encounterSelection, setEncounterSelection] = useState(settings.encounterSelection || 'all');
    const [selectedEncounters, setSelectedEncounters] = useState(settings.selectedEncounters || availableEncounters.map(e => e.id));
    // Update local state when props change (when user selects another node)
    useEffect(() => {
        // Explicitly define the default values when settings are not defined
        const defaultSettings = {
            enabled: false,
            probability: 10,
            encounterSelection: 'all',
            selectedEncounters: availableEncounters.map(e => e.id)
        };
        // Use settings if present, otherwise defaultSettings
        const currentSettings = settings || defaultSettings;
        setEnabled(currentSettings.enabled);
        setProbability(currentSettings.probability);
        setEncounterSelection(currentSettings.encounterSelection);
        setSelectedEncounters(currentSettings.selectedEncounters);
    }, [settings, availableEncounters]);
    // Now handle UI changes separately
    const handleEnabledChange = (e) => {
        const newEnabled = e.target.checked;
        setEnabled(newEnabled);
        // Immediately send the updated data upward
        onSettingsChange({
            enabled: newEnabled,
            probability,
            encounterSelection,
            selectedEncounters
        });
    };
    const handleProbabilityChange = (e) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value >= 0 && value <= 100) {
            setProbability(value);
            // Immediately send the updated data upward
            onSettingsChange({
                enabled,
                probability: value,
                encounterSelection,
                selectedEncounters
            });
        }
    };
    const handleSelectionTypeChange = (e) => {
        const value = e.target.value;
        setEncounterSelection(value);
        let newSelectedEncounters = selectedEncounters;
        // If 'all' is selected, automatically select all available encounters
        if (value === 'all') {
            newSelectedEncounters = availableEncounters.map(e => e.id);
            setSelectedEncounters(newSelectedEncounters);
        }
        // Immediately send the updated data upward
        onSettingsChange({
            enabled,
            probability,
            encounterSelection: value,
            selectedEncounters: newSelectedEncounters
        });
    };
    const handleEncounterToggle = (encounterId, checked) => {
        let newSelectedEncounters;
        if (checked) {
            newSelectedEncounters = [...selectedEncounters, encounterId];
        }
        else {
            newSelectedEncounters = selectedEncounters.filter(id => id !== encounterId);
        }
        setSelectedEncounters(newSelectedEncounters);
        // Immediately send the updated data upward
        onSettingsChange({
            enabled,
            probability,
            encounterSelection,
            selectedEncounters: newSelectedEncounters
        });
    };
    // Render event characteristics as colored badges
    const renderCharacteristicBadges = (event) => {
        if (!event.encounters || event.encounters.length === 0)
            return null;
        return (_jsx("div", { className: "characteristic-badges", children: event.encounters.map((option, index) => {
                const color = ABILITY_COLORS[option.ability] || '#ccc';
                return (_jsxs("span", { className: "characteristic-badge", style: { color: color }, children: [option.ability, " ", option.difficulty] }, index));
            }) }));
    };
    // Get event image URL
    const getEventImageUrl = (event) => {
        const abs = (u) => u.startsWith('http') ? u : `${backendUrl}${u}`;
        if (event.cardImage) {
            return abs(event.cardImage);
        }
        else if (event.backgroundImage) {
            return abs(event.backgroundImage);
        }
        return null;
    };
    return (_jsxs("div", { className: "panel-section resource-scanner-section random-encounters-section", children: [_jsxs("div", { className: "control-group checkbox-group", children: [_jsx("input", { type: "checkbox", id: `random-encounters-${coords.x}-${coords.y}`, checked: enabled, onChange: handleEnabledChange }), _jsx("label", { htmlFor: `random-encounters-${coords.x}-${coords.y}`, children: "Random Encounters" })] }), enabled && (_jsxs("div", { className: "scannable-options", children: [_jsxs("div", { className: "selection-type-control", children: [_jsx("label", { htmlFor: `encounter-selection-${coords.x}-${coords.y}`, children: "Encounter Selection:" }), _jsxs("select", { id: `encounter-selection-${coords.x}-${coords.y}`, value: encounterSelection, onChange: handleSelectionTypeChange, className: "resource-select", children: [_jsx("option", { value: "all", children: "All Encounters" }), _jsx("option", { value: "custom", children: "Custom" })] })] }), _jsxs("div", { className: "selection-type-control", children: [_jsx("label", { htmlFor: `probability-${coords.x}-${coords.y}`, children: "Encounter probability:" }), _jsxs("div", { className: "probability-input-container", children: [_jsx("input", { type: "number", id: `probability-${coords.x}-${coords.y}`, min: "0", max: "100", value: probability, onChange: handleProbabilityChange }), _jsx("span", { className: "percentage-sign", children: "%" })] })] }), encounterSelection === 'all' ? (_jsx("div", { className: "selected-resources-summary", children: _jsxs("span", { className: "all-resources-label", children: ["All ", availableEncounters.length, " encounters selected"] }) })) : (_jsxs("div", { className: "custom-resources-selection", children: [_jsx("div", { className: "resources-grid encounters-grid", children: availableEncounters.map(encounter => {
                                    const imageUrl = getEventImageUrl(encounter);
                                    return (_jsxs("div", { className: "resource-checkbox-item", children: [_jsx("input", { type: "checkbox", id: `encounter-${encounter.id}-${coords.x}-${coords.y}`, checked: selectedEncounters.includes(encounter.id), onChange: (e) => handleEncounterToggle(encounter.id, e.target.checked) }), _jsxs("label", { htmlFor: `encounter-${encounter.id}-${coords.x}-${coords.y}`, children: [_jsx("div", { className: "encounter-image", style: imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined, children: !imageUrl && (_jsx("div", { className: "image-placeholder", children: _jsx(FaImage, {}) })) }), _jsxs("div", { className: "encounter-info", children: [_jsx("div", { className: "encounter-title", children: encounter.title }), renderCharacteristicBadges(encounter)] })] })] }, encounter.id));
                                }) }), availableEncounters.length === 0 && (_jsx("div", { className: "no-resources-message", children: "No available encounters. Create them in \"Events\" tab." })), availableEncounters.length > 0 && (_jsx("div", { className: "selected-resources-summary", children: _jsxs("span", { className: "resource-count-label", children: [selectedEncounters.length, " of ", availableEncounters.length, " encounters selected"] }) }))] }))] }))] }));
};
export default RandomEncountersSettings;
